import { useCallback, useEffect, useMemo, useRef } from "react";
import type { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import { TaskTemplateAssigneeRole } from "@common/types/taskTemplate";
import {
    CommandsDirectory,
    InjectedCommandOption,
} from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import type { ApplyCloseTemplateEntityRow } from "./applyCloseTemplate.schema";
import { organizationMemberHasWorkspaceAccess } from "./applyCloseTemplateOrganizationMembers";

interface UseApplyCloseTemplateEntityBulkActionsParams {
    selectedBulkIds: string[];
    /** workspaceId for each selected entity id — used to compute the member intersection. */
    selectedEntityWorkspaceIds: string[];
    activeRoles: Set<TaskTemplateAssigneeRole>;
    members: OrganizationMember[];
    getMemberLabel: (userId: number | null) => string;
    onBulkUpdate: (
        ids: string[],
        field: keyof Pick<
            ApplyCloseTemplateEntityRow,
            "preparerId" | "reviewerId" | "managerId"
        >,
        value: number | null,
    ) => void;
    clearBulkSelection: () => void;
}

export function useApplyCloseTemplateEntityBulkActions({
    selectedBulkIds,
    selectedEntityWorkspaceIds,
    activeRoles,
    members,
    getMemberLabel,
    onBulkUpdate,
    clearBulkSelection,
}: UseApplyCloseTemplateEntityBulkActionsParams) {
    const {
        open: openCommands,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();

    /**
     * Members who have access to every workspace represented in the current
     * selection. An empty array means there is no overlap — only "None" will
     * be offered.
     */
    const eligibleMembers = useMemo(() => {
        if (selectedEntityWorkspaceIds.length === 0) {
            return members;
        }
        const uniqueWorkspaceIds = [...new Set(selectedEntityWorkspaceIds)];
        return members.filter((m) =>
            uniqueWorkspaceIds.every((wsId) =>
                organizationMemberHasWorkspaceAccess(m, wsId),
            ),
        );
    }, [members, selectedEntityWorkspaceIds]);

    const buildMemberOptions = useCallback(
        (
            field: keyof Pick<
                ApplyCloseTemplateEntityRow,
                "preparerId" | "reviewerId" | "managerId"
            >,
        ): InjectedCommandOption[] => {
            const noneOption: InjectedCommandOption = {
                id: `apply-template-entity-${field}-none`,
                label: "None",
                keywords: ["none", "clear", "remove", "unassign"],
                onSelect: () => {
                    onBulkUpdate(selectedBulkIds, field, null);
                    clearBulkSelection();
                },
            };

            const memberOptions: InjectedCommandOption[] = eligibleMembers.map(
                (m) => {
                    const label = getMemberLabel(m.user.id);
                    return {
                        id: `apply-template-entity-${field}-${m.user.id}`,
                        label,
                        keywords: [label.toLowerCase(), m.user.email ?? ""],
                        onSelect: () => {
                            onBulkUpdate(selectedBulkIds, field, m.user.id);
                            clearBulkSelection();
                        },
                    };
                },
            );

            return [noneOption, ...memberOptions];
        },
        [
            selectedBulkIds,
            eligibleMembers,
            getMemberLabel,
            onBulkUpdate,
            clearBulkSelection,
        ],
    );

    const preparerOptions = useMemo(
        () =>
            activeRoles.has(TaskTemplateAssigneeRole.PREPARER)
                ? buildMemberOptions("preparerId")
                : undefined,
        [activeRoles, buildMemberOptions],
    );

    const reviewerOptions = useMemo(
        () =>
            activeRoles.has(TaskTemplateAssigneeRole.REVIEWER)
                ? buildMemberOptions("reviewerId")
                : undefined,
        [activeRoles, buildMemberOptions],
    );

    const managerOptions = useMemo(
        () =>
            activeRoles.has(TaskTemplateAssigneeRole.MANAGER)
                ? buildMemberOptions("managerId")
                : undefined,
        [activeRoles, buildMemberOptions],
    );

    const buildCommandOptions = useCallback(
        () => ({
            selectedApplyTemplateEntityCount: selectedBulkIds.length,
            applyTemplateEntityBulkPreparerOptions: preparerOptions,
            applyTemplateEntityBulkReviewerOptions: reviewerOptions,
            applyTemplateEntityBulkManagerOptions: managerOptions,
        }),
        [
            selectedBulkIds.length,
            preparerOptions,
            reviewerOptions,
            managerOptions,
        ],
    );

    const handleOpenBulkActions = useCallback(() => {
        if (!selectedBulkIds.length) {
            return;
        }
        openCommands(
            CommandsDirectory.APPLY_TEMPLATE_ENTITY_BULK,
            buildCommandOptions(),
        );
    }, [openCommands, selectedBulkIds.length, buildCommandOptions]);

    const handleOpenBulkPreparer = useCallback(() => {
        if (!selectedBulkIds.length) {
            return;
        }
        openCommands(
            CommandsDirectory.APPLY_TEMPLATE_ENTITY_BULK_PREPARER,
            buildCommandOptions(),
        );
    }, [openCommands, selectedBulkIds.length, buildCommandOptions]);

    const handleOpenBulkReviewer = useCallback(() => {
        if (!selectedBulkIds.length) {
            return;
        }
        openCommands(
            CommandsDirectory.APPLY_TEMPLATE_ENTITY_BULK_REVIEWER,
            buildCommandOptions(),
        );
    }, [openCommands, selectedBulkIds.length, buildCommandOptions]);

    const handleOpenBulkManager = useCallback(() => {
        if (!selectedBulkIds.length) {
            return;
        }
        openCommands(
            CommandsDirectory.APPLY_TEMPLATE_ENTITY_BULK_MANAGER,
            buildCommandOptions(),
        );
    }, [openCommands, selectedBulkIds.length, buildCommandOptions]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (selectedBulkIds.length > 0) {
            registerOpenOverride(() => handleOpenBulkActionsRef.current());
            return () => unregisterOpenOverride();
        }
        unregisterOpenOverride();
    }, [selectedBulkIds.length, registerOpenOverride, unregisterOpenOverride]);

    return {
        handleOpenBulkActions,
        handleOpenBulkPreparer,
        handleOpenBulkReviewer,
        handleOpenBulkManager,
    };
}
